// M8-ASSESS queries: keys under `["company", co, "assessments"]` (the change pings of
// assessment_round / _participant / _proposal invalidate them), and the respondent's view by its
// token (no company session: `["respond", co, token]`).
import { ApiError } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export const assessKeys = {
  all: (co: string) => ["company", co, "assessments"] as const,
  rounds: (co: string) => ["company", co, "assessments", "rounds"] as const,
  round: (co: string, id: string) => ["company", co, "assessments", "round", id] as const,
  report: (co: string, id: string) => ["company", co, "assessments", "report", id] as const,
  mine: (co: string) => ["company", co, "assessments", "mine"] as const,
  people: (co: string) => ["company", co, "assessments", "people"] as const,
  respond: (co: string, token: string) => ["respond", co, token] as const,
};

export const roundsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: assessKeys.rounds(co),
    queryFn: () => api.client.assessments.listRounds({ params: { co } }),
  });

export const roundQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: assessKeys.round(co, id),
    queryFn: () => api.client.assessments.getRound({ params: { co, id } }),
  });

export const reportQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: assessKeys.report(co, id),
    queryFn: () => api.client.assessments.getReport({ params: { co, id } }),
  });

/** The caller's own pending questionnaires; a refusal (a key, an operator) reads as none. */
export const mineQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: assessKeys.mine(co),
    queryFn: async () => {
      try {
        return (await api.client.assessments.mine({ params: { co } })).data;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return [];
        throw e;
      }
    },
  });

/** The rounds for a manager's Home card; null when the caller may not read them. */
export const homeRoundsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: assessKeys.rounds(co),
    queryFn: async () => {
      try {
        return await api.client.assessments.listRounds({ params: { co } });
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return null;
        throw e;
      }
    },
  });

/** The active people a round may ask (the directory, one page of 200). */
export const assessPeopleQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: assessKeys.people(co),
    queryFn: async () =>
      (await api.client.people.list({ params: { co }, query: { limit: 200 } })).data.filter(
        (p) => p.kind === "person" && p.status === "active",
      ),
  });

export const respondQuery = (api: Api, co: string, token: string) =>
  queryOptions({
    queryKey: assessKeys.respond(co, token),
    queryFn: () => api.client.respond.get({ params: { co, token } }),
    retry: false,
    refetchOnWindowFocus: false,
  });
